'use client'
import React, { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import { IScene, IActor, ICinematics } from '@/types'
import { clarity, CLARITY_TAGS } from '@/utils/clarity'
import { ErrorBoundary } from './ErrorBoundary'
import { GeoLocationGuard } from '@/components/GeoLocationGuard'
import { AuthGuard } from '@/components/AuthGuard'
import { useCinematicsStore } from './stores/CinematicsStore'
import { normalizeAuthRequire } from './utils/sceneAuth'
import { useQueryClient } from '@tanstack/react-query'
import { notFound } from 'next/navigation'
import { usePathname } from 'next/navigation'

interface IDirectorProps {
  ActorComponents: (props: any) => React.JSX.Element | null,
  children: React.ReactNode
  defaultSceneId?: string
}

export const Director: React.FC<IDirectorProps> = (props) => {
  const {
    ActorComponents,
    children,
    defaultSceneId
  } = props
  const {
    scene: pathnameMatchScene,
    cinematics,
    initialLanguage,
    scenes
  }: {
    scene: IScene | undefined
    cinematics: ICinematics
    initialLanguage: string
    scenes: { [key: string]: IScene }
  } = useCinematicsStore() || {}

  const pathname = usePathname()
  const { ambiance } = cinematics

  // First try exact match, then try subpath match
  const exactMatchScene = pathnameMatchScene
  const subpathScene = !exactMatchScene
    ? Object.values(scenes || {})
      .filter((scene: IScene) => {
        const sceneRoute = scene.route
        if (!sceneRoute) return false

        // Ensure pathname starts with sceneRoute AND the next character is '/' or end of string
        // This prevents false matches like /checkout matching /checkout-old
        return pathname.startsWith(sceneRoute) &&
          (pathname.length === sceneRoute.length || pathname[sceneRoute.length] === '/')
      })
      // Sort by route length (longest first) to prioritize more specific matches
      .sort((a, b) => (b.route?.length || 0) - (a.route?.length || 0))[0]
    : null

  const scene = exactMatchScene || subpathScene || Object.values(scenes || {})?.find(s => s._id === defaultSceneId)
  const actors: IActor[] = Object.values(scene?.actors || {})

  // Set cinematic in query client for easy access
  useQueryClient().setQueryData(['cinematics'], { ...cinematics, initialLanguage })

  // Tag the Clarity session with the matched scene so recordings/heatmaps can
  // be filtered per funnel (routes sharing button copy stay distinguishable).
  const sceneTag = scene?.route || scene?._id || ''
  useEffect(() => {
    if (sceneTag) clarity.set(CLARITY_TAGS.SCENE, sceneTag)
  }, [sceneTag])

  // Scene auth gate requirement, normalized to a safe value (see sceneAuth).
  const rawRequire = scene?.set?.auth?.require
  const { require: normalizedRequire, isKnown: isRequireKnown } = normalizeAuthRequire(rawRequire)
  // Report an out-of-enum value from an effect (never the render body — a scene
  // re-renders often and an inline captureException would flood Sentry). Kept
  // above the notFound() early-exit so this hook stays unconditional.
  const invalidRequire = rawRequire != null && !isRequireKnown ? String(rawRequire) : null
  useEffect(() => {
    if (invalidRequire === null) return
    Sentry.captureException(
      new Error(`Director: invalid scene auth.require "${invalidRequire}" — defaulting to authenticated`),
      { level: 'warning' }
    )
  }, [invalidRequire])

  if (cinematics?.scenes && !scene) {
    console.log('[Director] Scene not found', pathnameMatchScene)
    notFound()
  }

  // Scene-wide country gate. `set.geolocation` gates every actor in the scene
  // at once — the alternative (a rule per script entry) can't gate siblings,
  // since actors render as a flat list here. Absent/empty rule = no gate, and
  // an unknown country fails open (FZSPF-3792): a geo outage never blanks a page.
  const geoRule = scene?.set?.geolocation
  const hasGeoRule = Boolean(geoRule?.allow?.length || geoRule?.deny?.length)

  // Scene-wide auth gate (PRO-371), same rationale as the geo gate above.
  // Composed *inside* the geo gate — geo is evaluated first — since a
  // geo-blocked visitor should see the geo message regardless of auth state.
  const authRule = scene?.set?.auth
  const hasAuthRule = Boolean(authRule?.require)

  const actorNodes = actors.map((actor, i) => {
    const { _id, viewId } = actor
    const script = scene?.script.find(s => s.actorId === _id)
    const set = scene?.set

    return (
      <React.Fragment key={_id}>
        <ErrorBoundary viewId={viewId}>
          <ActorComponents {...{ ...actor, script: { ...script }, index: i, set, ambiance }} />
        </ErrorBoundary>
      </React.Fragment>
    )
  })

  let gatedActorNodes: React.ReactNode = actorNodes

  if (hasAuthRule && authRule) {
    gatedActorNodes = (
      <AuthGuard
        require={normalizedRequire}
        fallback={scene?.set?.authFallback}
        redirectTo={scene?.set?.authRedirect}
      >
        {gatedActorNodes}
      </AuthGuard>
    )
  }

  if (hasGeoRule) {
    gatedActorNodes = (
      <GeoLocationGuard
        allow={geoRule?.allow || []}
        deny={geoRule?.deny || []}
        // `hidden` renders nothing at all; anything else shows the default
        // "not available in your location" block once for the whole scene.
        message={scene?.set?.geoFallback === 'hidden' ? null : undefined}
      >
        {gatedActorNodes}
      </GeoLocationGuard>
    )
  }

  return (
    <>
      {gatedActorNodes}
      {children}
    </>
  )
}